import { create } from 'zustand';
import type {
  MasterData,
  Airport,
  Country,
  Carrier,
  CountryStateGroup,
  ResourceList,
  Questionnaire,
  CodeTypeList,
  MealsList,
  EquipmentTypesList,
  FareBrandsList,
  ExceptionList,
  RouteMessagesList,
  AnnouncementsList,
  OlciBanners,
  OfferCarouselsList,
  LifeBannersList,
  UpgradeOffersList,
  DetailUpgradeList,
  RandomSeatSelectionRules,
  AppSettings,
  TerminalDisplay,
  Preferences,
  DocumentList,
} from '@models/master-data/master-data.model';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS } from '@utils/session-keys.constants';
import { logger } from '@utils/logger.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isMasterDataValid = (data: unknown): data is MasterData => {
  return isRecord(data);
};

interface MasterDataState {
  masterData: MasterData | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

interface MasterDataActions {
  setMasterData: (data: MasterData) => void;
  setPreferences: (preferences: Preferences) => void;
  clearMasterData: () => void;
  setLoading: (value: boolean) => void;
  setError: (error: string | null) => void;
  hydrate: () => void;
}

type MasterDataStore = MasterDataState & MasterDataActions;

export const useMasterDataStore = create<MasterDataStore>((set) => ({
  masterData: null,
  isLoaded: false,
  isLoading: false,
  error: null,

  setMasterData: (data): void => {
    sessionStorageUtil.set(SESSION_KEYS.MASTER_DATA, data);
    set({ masterData: data, isLoaded: true, error: null });
  },

  setPreferences: (preferences): void => {
    set((state) => ({
      masterData: state.masterData ? { ...state.masterData, preferences } : null,
    }));
  },

  clearMasterData: (): void => {
    sessionStorageUtil.remove(SESSION_KEYS.MASTER_DATA);
    set({ masterData: null, isLoaded: false });
  },

  setLoading: (value): void => set({ isLoading: value }),
  setError: (error): void => set({ error, isLoading: false }),

  hydrate: (): void => {
    const cached = sessionStorageUtil.get<MasterData>(SESSION_KEYS.MASTER_DATA);
    //Validating the master data is present in cache or not
    if (cached && isMasterDataValid(cached)) {
      logger.info('Master data loaded from session storage and validated successfully');
      set({ masterData: cached, isLoaded: true });
    } else if (cached) {
      logger.warn('Master data in session storage failed validation; will be reloaded from API');
      sessionStorageUtil.remove(SESSION_KEYS.MASTER_DATA);
      set({ masterData: null, isLoaded: false });
    }
  },
}));

export const selectMasterData = (state: MasterDataStore): MasterData | null => state.masterData;
export const selectAirports = (state: MasterDataStore): Airport[] =>
  state.masterData?.airports ?? [];
export const selectCountries = (state: MasterDataStore): Country[] =>
  state.masterData?.countries ?? [];
export const selectGroupedStates = (state: MasterDataStore): CountryStateGroup[] =>
  state.masterData?.groupedStates ?? [];
export const selectCarriers = (state: MasterDataStore): Carrier[] =>
  state.masterData?.carriers ?? [];
export const selectResourceLists = (state: MasterDataStore): ResourceList =>
  state.masterData?.resourceLists ?? {};
export const selectDocumentList = (state: MasterDataStore): DocumentList =>
  state.masterData?.documentList ?? [];
export const selectQuestionnaire = (state: MasterDataStore): Questionnaire =>
  state.masterData?.questionnaire ?? {};
export const selectCodeTypeList = (state: MasterDataStore): CodeTypeList =>
  state.masterData?.codeTypeList ?? [];
export const selectMealsList = (state: MasterDataStore): MealsList =>
  state.masterData?.mealsList ?? [];
export const selectEquipmentTypesList = (state: MasterDataStore): EquipmentTypesList =>
  state.masterData?.equipmentTypesList ?? [];
export const selectFareBrands = (state: MasterDataStore): FareBrandsList =>
  state.masterData?.fareBrands ?? [];
export const selectExceptionList = (state: MasterDataStore): ExceptionList =>
  state.masterData?.exceptionList ?? {};
export const selectRouteMessages = (state: MasterDataStore): RouteMessagesList =>
  state.masterData?.routeMessages ?? [];
export const selectAnnouncements = (state: MasterDataStore): AnnouncementsList =>
  state.masterData?.announcements ?? [];
export const selectOlciBanners = (state: MasterDataStore): OlciBanners =>
  state.masterData?.olciBanners ?? [];
export const selectOffers = (state: MasterDataStore): OfferCarouselsList =>
  state.masterData?.offers ?? [];
export const selectLifeBanners = (state: MasterDataStore): LifeBannersList =>
  state.masterData?.lifeBanners ?? [];
export const selectUpgradeOffers = (state: MasterDataStore): UpgradeOffersList =>
  state.masterData?.upgradeOffers ?? [];
export const selectDetailUpgrades = (state: MasterDataStore): DetailUpgradeList =>
  state.masterData?.detailUpgrades ?? [];
export const selectRandomSeatSelectionRules = (state: MasterDataStore): RandomSeatSelectionRules =>
  state.masterData?.randomSeatSelectionRules ?? {
    description1: '',
    description2: '',
    description3: '',
  };
export const selectAppSettings = (state: MasterDataStore): AppSettings =>
  state.masterData?.appSettings ?? {};
export const selectTerminals = (state: MasterDataStore): TerminalDisplay[] =>
  state.masterData?.terminals ?? [];
export const selectPreferences = (state: MasterDataStore): Preferences =>
  state.masterData?.preferences ?? {};
export const selectPreferredCountries = (state: MasterDataStore): readonly string[] =>
  state.masterData?.preferences?.preferredCountries ?? [];
export const selectPreferredServices = (state: MasterDataStore): readonly string[] =>
  state.masterData?.preferences?.preferredServices ?? [];
export const selectMasterDataLoaded = (state: MasterDataStore): boolean => state.isLoaded;
